import React from 'react';
import { FileText, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatCurrency } from '@/lib/data';
import { shortDay, statusLabel, type DashboardQuotation } from '../../useDashboard';

interface QuotationsWidgetProps {
  quotations: DashboardQuotation[];
}

const statusColors: Record<string, string> = {
  draft: 'bg-stone-100 text-stone-700',
  sent: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  converted: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  expired: 'bg-orange-100 text-orange-700',
};

export function QuotationsWidget({ quotations }: QuotationsWidgetProps) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-6">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="font-semibold text-stone-800">Recent quotations</h3>
          <p className="text-xs text-stone-500">Newest first</p>
        </div>
        <Link to="/quotations" className="inline-flex items-center h-10 px-2 -mr-2 text-sm text-amber-700 hover:text-amber-800 font-medium">
          All quotations
        </Link>
      </div>

      {quotations.length === 0 ? (
        <div className="text-center py-8">
          <FileText className="w-10 h-10 mx-auto mb-2 text-stone-300" />
          <p className="text-sm text-stone-600 font-medium">No quotations yet</p>
          <Link to="/quotations" className="mt-2 inline-flex items-center h-10 px-3 text-sm font-semibold text-amber-700 hover:underline">
            Open quotations
          </Link>
        </div>
      ) : (
        <div className="space-y-2">
          {quotations.map((q) => (
            <Link
              key={q.id}
              to={`/quotations/detail?id=${q.id}`}
              className="flex items-center gap-3 min-h-[56px] p-3 rounded-lg border border-stone-100 hover:border-stone-300 hover:bg-stone-50 transition-colors group"
            >
              <FileText className="w-4 h-4 text-stone-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-stone-800 truncate group-hover:text-amber-700">
                  {q.client_name || q.name || 'No client'}
                </p>
                <p className="text-xs text-stone-500 truncate mt-0.5">
                  {[q.quotation_number, q.name && q.name !== q.client_name ? q.name : '', q.event_date ? `for ${shortDay(q.event_date)}` : '']
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <span className="text-sm font-semibold text-stone-800 tabular-nums">{formatCurrency(q.total_amount)}</span>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${statusColors[q.status] || 'bg-stone-100 text-stone-700'}`}>
                  {statusLabel(q.status || 'draft')}
                </span>
              </div>
              <ChevronRight className="w-4 h-4 text-stone-400 group-hover:text-stone-600 flex-shrink-0" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
